import { CurrencyAmount, JSBI, Rounding } from '../sdk'
import { currencyAmountFromRaw } from './swapPriceImpactLimit'
import { toFlooredSwapAmount } from './swapAmountDisplay'

const FRACTION_BASIS = 10_000

function fractionNumerator(fraction: number): JSBI {
  return JSBI.BigInt(Math.round(fraction * FRACTION_BASIS))
}

/** Preset base: solo montos con reserva de gas ya aplicada (max spend > 0). */
export function resolvePresetSpendable(
  walletBalance: CurrencyAmount | undefined,
  maxSpend: CurrencyAmount | undefined,
): CurrencyAmount | undefined {
  if (!walletBalance?.greaterThan(JSBI.BigInt(0))) return undefined
  if (maxSpend?.greaterThan(JSBI.BigInt(0))) return maxSpend
  return undefined
}

export function spendableFractionAmount(spendable: CurrencyAmount, fraction: number): CurrencyAmount {
  if (fraction >= 1) return spendable
  return currencyAmountFromRaw(spendable.currency, fractionRawAmount(spendable, fraction))
}

/** Monto de preset listo para el input de swap (truncado hacia abajo, igual que al aplicar). */
export function spendableFractionSwapInput(spendable: CurrencyAmount, fraction: number): string {
  return toFlooredSwapAmount(spendableFractionAmount(spendable, fraction))
}

/** Raw spendable amount at a balance fraction (matches preset button logic). */
export function fractionRawAmount(spendable: CurrencyAmount, fraction: number): JSBI {
  if (fraction >= 1) return spendable.raw
  return JSBI.divide(
    JSBI.multiply(spendable.raw, fractionNumerator(fraction)),
    JSBI.BigInt(FRACTION_BASIS),
  )
}

/** Exact string for user input at a balance fraction. */
export function fractionExactAmount(spendable: CurrencyAmount, fraction: number): string {
  if (fraction >= 1) return spendable.toExact()
  return spendableFractionAmount(spendable, fraction).toFixed(
    spendable.currency.decimals,
    {},
    Rounding.ROUND_DOWN,
  )
}

/** Which preset fraction matches the typed amount string shown in the input. */
export function resolveActiveBalanceFractionFromTypedValue(
  typedValue: string,
  spendable: CurrencyAmount | undefined,
  fractions: readonly number[],
): number | null {
  const normalized = typedValue.trim()
  if (!normalized || !spendable) return null

  for (let i = fractions.length - 1; i >= 0; i--) {
    const fraction = fractions[i]!
    if (normalized === spendableFractionSwapInput(spendable, fraction)) {
      return fraction
    }
  }

  return null
}

/** Which preset fraction matches the typed amount, if any. */
export function resolveActiveBalanceFraction(
  amount: CurrencyAmount | undefined,
  spendable: CurrencyAmount | undefined,
  fractions: readonly number[],
): number | null {
  if (!amount || !spendable || !amount.greaterThan(JSBI.BigInt(0))) return null
  return resolveActiveBalanceFractionFromTypedValue(toFlooredSwapAmount(amount), spendable, fractions)
}
